import React, { useState } from 'react';
import { AvailabilityCalendar } from '../AvailabilityCalendar';
import { Loading } from '../Loading';
import { SmartImage } from '../SmartImage';
import styles from '../../utils/styles/components/DualAccessGateway/style.module.css';

const propositionHighlights = [
  'Interventions artistiques pour galas, festivals, jumpings et événements privés.',
  'Travail du cheval, du débourrage au perfectionnement.',
  'Une expertise éprouvée, au service de contextes scéniques comme équestres.'
];

const offers = [
  {
    audience: 'Pour les organisateurs.',
    background: '/images/cavalluna/sylvie_liberte_assis_regard_fond_unie.png',
    backgroundAlt: 'Sylvie Willms assise en liberté avec son cheval sur un fond uni',
    subject: '/images/cavalluna/sylvie_liberte_assis_regard_sujet.png',
    subjectAlt: 'Sujet détouré de Sylvie Willms assise en liberté avec son cheval',
    note: 'L’enjeu n’est pas seulement de montrer un cheval, mais de donner une tenue, un imaginaire et une vraie présence à un événement.',
    summary: 'Une proposition artistique pour la scène et l’événement.',
    title: 'Spectacles et numéros équestres.',
    points: [
      {
        label: 'Formats',
        text: 'Galas, festivals, salons, jumpings, rendez-vous institutionnels ou privés.'
      },
      {
        label: 'Promesse',
        text: 'Une présence équestre élégante, lisible et adaptable selon le lieu, le public et le tempo de la programmation.'
      },
      {
        label: 'Repère',
        text: 'Une artiste habituée aux tournées, aux grandes scènes et aux contextes qui demandent une vraie maîtrise du cadre.'
      }
    ],
    availabilityHeading: 'Disponibilités.',
    availabilityText:
      'Un calendrier de disponibilité pensé pour donner une lecture claire des périodes déjà engagées et des fenêtres encore ouvertes.',
    availabilityToggleLabel: 'Consulter les disponibilités',
    tone: 'scene'
  },
  {
    audience: 'Pour les propriétaires.',
    background: '/images/biographie/sylvie_lidador.jpg',
    backgroundAlt: 'Sylvie Willms avec un cheval blanc, image source candidate au détourage',
    installationsHeading: 'Le cadre.',
    installationsText:
      'Un lieu pensé pour le travail du cheval: des espaces clairs, soignés et ouverts, où la progression peut s’inscrire dans le calme, la régularité et le temps long.',
    installationsToggleLabel: 'Découvrir les installations.',
    installations: [
      {
        title: 'Carrières et espaces de travail.',
        text: 'Des aires de travail ouvertes où chaque séance trouve son rythme, du premier cadre posé au perfectionnement d’un cheval déjà formé.',
        images: [
          {
            src: '/images/installations/installation-01.jpg',
            alt: 'Vue des installations de Sylvie Willms, carrière extérieure.'
          },
          {
            src: '/images/installations/installation-03.jpg',
            alt: 'Vue des installations de Sylvie Willms, carrière bordée de clôtures en bois.'
          },
          {
            src: '/images/installations/installation-05.jpg',
            alt: 'Vue des installations de Sylvie Willms, sol de carrière.'
          }
        ]
      },
      {
        title: 'Paddocks, clôtures et circulation.',
        text: 'Une organisation sobre et lisible, pensée pour les sorties, les déplacements et l’équilibre du quotidien, sans jamais dissocier le confort du cheval de la qualité du travail.',
        images: [
          {
            src: '/images/installations/installation-02.jpg',
            alt: 'Vue des installations de Sylvie Willms, paddock et clôtures.'
          },
          {
            src: '/images/installations/installation-04.jpg',
            alt: 'Vue des installations de Sylvie Willms, espace de travail extérieur.'
          },
          {
            src: '/images/installations/installation-06.jpg',
            alt: 'Vue des installations de Sylvie Willms, clôtures et environnement boisé.'
          }
        ]
      },
      {
        title: 'Cadre général et environnement.',
        text: 'Du calme, de l’air et de l’espace: un environnement qui soutient la disponibilité du cheval, la concentration dans le travail et la continuité d’une progression menée avec exigence.',
        images: [
          {
            src: '/images/installations/installation-07.jpg',
            alt: 'Vue des installations de Sylvie Willms, infrastructure équestre extérieure.'
          },
          {
            src: '/images/installations/installation-08.jpg',
            alt: 'Vue des installations de Sylvie Willms, prairie attenante.'
          },
          {
            src: '/images/installations/installation-09.jpg',
            alt: 'Vue des installations de Sylvie Willms, environnement de travail extérieur.'
          }
        ]
      }
    ],
    note: 'Ici, tout repose moins sur l’effet que sur la justesse du geste, la continuité du travail et l’attention portée au cheval.',
    reverse: true,
    summary: 'Un travail suivi, précis et durable.',
    title: 'Pension-travail et accompagnement du cheval.',
    points: [
      {
        label: 'Travail',
        text: 'Débourrage, remise en route, dressage, obstacle, préparation pour le spectacle ou la compétition.'
      },
      {
        label: 'Approche',
        text: 'Un accompagnement ajusté au tempérament du cheval, à son niveau et à l’objectif poursuivi.'
      },
      {
        label: 'Cadre',
        text: 'Une progression construite avec patience, exigence et qualité de relation.'
      }
    ],
    tone: 'work'
  }
];

export const DualAccessGateway = ({
  shows = [],
  showsLoading = false,
  showsError = false,
  availabilityBlocks = [],
  availabilityLoading = false,
  availabilityError = false,
  forceExpandedPanels = false
}) => {
  const [openPanels, setOpenPanels] = useState({});

  const handlePanelToggle = (panelId) => (event) => {
    const { open } = event.currentTarget;

    setOpenPanels((currentPanels) => (
      currentPanels[panelId] === open
        ? currentPanels
        : { ...currentPanels, [panelId]: open }
    ));
  };

  return (
    <div className={styles.shell}>
      <div className={styles.introPanel}>
        <div className={styles.introCopy}>
          <span className={styles.kicker}>Clarifier les possibilités.</span>
          <h3 className={styles.title}>Deux domaines d’intervention, une même exigence.</h3>
          <p className={styles.text}>
            L’activité de Sylvie Willms s’articule autour de deux propositions distinctes. La première concerne
            les prestations artistiques destinées à la scène, à l’événementiel et aux rendez-vous publics ou privés.
            La seconde relève du travail du cheval, conduit dans la durée, du débourrage au perfectionnement.
          </p>
        </div>

        <ul className={styles.introList}>
          {propositionHighlights.map((item) => (
            <li className={styles.introListItem} key={item}>
              <span className={styles.introListMarker} />
              <span>{item}</span>
            </li>
          ))}
        </ul>
      </div>

      <div className={styles.offerStack}>
        {offers.map((item) => {
          const hasLayeredVisual = Boolean(item.subject);
          const availabilityPanelId = `${item.title}-availability`;
          const installationsPanelId = `${item.title}-installations`;
          const isAvailabilityOpen = Boolean(forceExpandedPanels || openPanels[availabilityPanelId]);
          const isInstallationsOpen = Boolean(forceExpandedPanels || openPanels[installationsPanelId]);

          return (
            <article
              className={`${styles.offerRow} ${item.reverse ? styles.offerRowReverse : ''} ${item.tone === 'work' ? styles.offerRowWork : ''} ${hasLayeredVisual ? styles.offerRowLayered : ''}`}
              key={item.title}
            >
              <div className={`${styles.offerContent} ${hasLayeredVisual ? styles.offerContentLayered : ''}`}>
                <span className={styles.offerAudience}>{item.audience}</span>
                <h4 className={styles.offerTitle}>{item.title}</h4>
                <p className={styles.offerSummary}>{item.summary}</p>
                <p className={styles.offerText}>{item.note}</p>

                {!hasLayeredVisual ? (
                  <dl className={styles.offerFacts}>
                    {item.points.map((point) => (
                      <div className={styles.offerFact} key={point.label}>
                        <dt className={styles.offerFactLabel}>{point.label}</dt>
                        <dd className={styles.offerFactText}>{point.text}</dd>
                      </div>
                    ))}
                  </dl>
                ) : null}
              </div>

              <div className={`${styles.offerVisual} ${hasLayeredVisual ? styles.offerVisualLayered : ''}`}>
                <div className={`${styles.visualFrame} ${hasLayeredVisual ? styles.visualFrameLayered : ''}`}>
                  <div className={styles.visualBackgroundFrame}>
                    <SmartImage
                      alt={item.backgroundAlt}
                      className={`${styles.visualBackground} ${hasLayeredVisual ? styles.visualBackgroundLayered : ''}`}
                      decoding="async"
                      loading="lazy"
                      src={item.background}
                    />
                  </div>
                  {hasLayeredVisual ? (
                    <SmartImage
                      alt={item.subjectAlt || item.backgroundAlt}
                      className={`${styles.visualSubject} ${styles.visualSubjectLayered}`}
                      decoding="async"
                      loading="lazy"
                      src={item.subject}
                    />
                  ) : null}
                  <div className={styles.visualShade} />
                </div>
              </div>

              {hasLayeredVisual ? (
                <dl className={`${styles.offerFacts} ${styles.offerFactsLayered}`}>
                  {item.points.map((point) => (
                    <div className={`${styles.offerFact} ${styles.offerFactBottom}`} key={point.label}>
                      <dt className={styles.offerFactLabel}>{point.label}</dt>
                      <dd className={styles.offerFactText}>{point.text}</dd>
                    </div>
                  ))}
                </dl>
              ) : null}

              {item.availabilityToggleLabel ? (
                <section className={styles.disclosurePanel}>
                  <details
                    className={styles.disclosure}
                    onToggle={forceExpandedPanels ? undefined : handlePanelToggle(availabilityPanelId)}
                    open={isAvailabilityOpen}
                  >
                    <summary className={styles.disclosureToggle}>{item.availabilityToggleLabel}</summary>

                    {isAvailabilityOpen ? (
                      <div className={styles.disclosureContent}>
                        <div className={styles.disclosureIntro}>
                          <span className={styles.disclosureKicker}>{item.availabilityHeading}</span>
                          <p className={styles.disclosureText}>{item.availabilityText}</p>
                        </div>

                        {showsLoading || availabilityLoading ? <Loading label="Chargement du calendrier..." /> : null}
                        {!showsLoading && !availabilityLoading && (showsError || availabilityError) ? (
                          <p className={styles.disclosureMessage}>Impossible de retrouver les prochaines disponibilités pour le moment.</p>
                        ) : null}
                        {!showsLoading && !availabilityLoading && !showsError && !availabilityError ? (
                          <AvailabilityCalendar availabilityBlocks={availabilityBlocks} shows={shows} />
                        ) : null}
                      </div>
                    ) : null}
                  </details>
                </section>
              ) : null}

              {item.installations?.length ? (
                <section className={styles.installationsPanel}>
                  <details
                    className={styles.disclosure}
                    onToggle={forceExpandedPanels ? undefined : handlePanelToggle(installationsPanelId)}
                    open={isInstallationsOpen}
                  >
                    <summary className={styles.disclosureToggle}>{item.installationsToggleLabel}</summary>

                    {isInstallationsOpen ? (
                      <div className={styles.installationsContent}>
                        <div className={styles.installationsIntro}>
                          <span className={styles.installationsKicker}>{item.installationsHeading}</span>
                          <p className={styles.installationsText}>{item.installationsText}</p>
                        </div>

                        <div className={styles.installationsStack}>
                          {item.installations.map((group) => (
                            <article className={styles.installationFeature} key={group.title}>
                              <h5 className={styles.installationFeatureTitle}>{group.title}</h5>
                              <p className={styles.installationFeatureText}>{group.text}</p>

                              <div className={styles.installationCarouselViewport}>
                                <div className={styles.installationCarouselTrack}>
                                  {group.images.map((image) => (
                                    <figure className={styles.installationCard} key={image.src}>
                                      <SmartImage
                                        alt={image.alt}
                                        className={styles.installationImage}
                                        decoding="async"
                                        loading="lazy"
                                        src={image.src}
                                      />
                                    </figure>
                                  ))}
                                </div>
                              </div>
                            </article>
                          ))}
                        </div>
                      </div>
                    ) : null}
                  </details>
                </section>
              ) : null}
            </article>
          );
        })}
      </div>
    </div>
  );
};
